import { useEffect, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Loading from '@/components/common/Loading'
import ClaimTeamFlow from '@/components/scrimmage/ClaimTeamFlow'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

/**
 * ClaimTeam Page
 * 
 * Standalone page for claiming a scraped/seed team.
 * Can be accessed via:
 * - /claim-team/:teamId - Claim specific team by ID
 * - /claim-team?search=TeamName - Search for team to claim
 */
export default function ClaimTeam() {
  const { teamId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [team, setTeam] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [user, setUser] = useState(null)
  const [existingTeam, setExistingTeam] = useState(null)
  const [requestAccessTeam, setRequestAccessTeam] = useState(null)
  const [requestingAccess, setRequestingAccess] = useState(false)

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    if (teamId) {
      loadTeam(teamId)
    } else if (searchQuery) {
      searchTeams(searchQuery)
    } else {
      setLoading(false)
    }
  }, [teamId, searchQuery])

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      toast.error('Please sign in to claim a team')
      navigate('/auth?tab=signin&redirect=/claim-team')
      return
    }
    setUser(session.user)

    // Check if user already has a team
    // Use maybeSingle() instead of single() to avoid 406 errors when no team exists
    const { data: userTeam, error: teamError } = await supabase
      .from('teams')
      .select('*')
      .eq('user_id', session.user.id)
      .maybeSingle()

    if (teamError) {
      console.warn('Error checking for existing team:', teamError)
    }
    
    if (userTeam) {
      setExistingTeam(userTeam)
    }
  }

  const loadTeam = async (id) => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      if (data.claimed_at) {
        toast.error('This team has already been claimed')
        navigate('/scrimmages/bulletin')
        return
      }

      if (data.data_source !== 'seed_auto') {
        toast.error('This team cannot be claimed')
        navigate('/scrimmages/bulletin')
        return
      }

      setTeam(data)
    } catch (error) {
      console.error('Error loading team:', error)
      toast.error('Team not found')
    } finally {
      setLoading(false)
    }
  }

  const searchTeams = async (query) => {
    setLoading(true)
    try {
      // Use the new search_claimable_teams RPC
      // This returns both claimed and unclaimed teams with proper ordering
      const { data, error } = await supabase
        .rpc('search_claimable_teams', {
          search_term: query,
          filter_level: 'boys_squirt_10u',  // TODO: Make this a filter
          filter_level_detail: null,
          filter_state: 'MN',
          max_results: 20
        })

      if (error) throw error

      setSearchResults(data || [])
    } catch (error) {
      console.error('Error searching teams:', error)
      // Fallback to direct query if RPC fails
      try {
        const { data } = await supabase
          .from('teams')
          .select('*')
          .eq('data_source', 'seed_auto')
          .ilike('name', `%${query}%`)
          .order('name')
          .limit(20)
        setSearchResults(data || [])
      } catch {
        toast.error('Search failed')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      searchTeams(searchQuery)
    }
  }

  const handleClaimSuccess = () => {
    // Redirect to scrimmages manager after claim
    setTimeout(() => {
      navigate('/scrimmages/manage')
    }, 2000)
  }

  const handleRequestAccess = (claimedTeam) => {
    setRequestAccessTeam(claimedTeam)
  }

  const submitAccessRequest = async () => {
    if (!requestAccessTeam || !user) return
    
    setRequestingAccess(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Please sign in to request access')
        return
      }

      // Call edge function to send request
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-team-access`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            teamId: requestAccessTeam.id
          })
        }
      )

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Request failed')
      }

      toast.success('Access request sent! The team manager will be notified.')
      setRequestAccessTeam(null)
    } catch (error) {
      console.error('Access request error:', error)
      toast.error(error.message || 'Failed to send access request')
    } finally {
      setRequestingAccess(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <Loading text="Loading..." />
      </div>
    )
  }

  // Request Access Modal
  if (requestAccessTeam) {
    return (
      <div className="min-h-screen bg-brand-cream py-12">
        <Container>
          <Card className="max-w-lg mx-auto p-6">
            <button 
              onClick={() => setRequestAccessTeam(null)} 
              className="text-sm text-brand-slate hover:text-brand-charcoal mb-4"
            >
              ← Back to search
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">👋</span>
              </div>
              <h2 className="font-heading text-xl font-bold text-brand-charcoal mb-2">
                Request Team Access
              </h2>
              <p className="text-brand-slate">
                <strong>{requestAccessTeam.name}</strong> is already managed by{' '}
                {requestAccessTeam.claimed_by_name || 'another user'}.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>What happens next:</strong>
              </p>
              <ul className="text-sm text-amber-700 mt-2 space-y-1">
                <li>• The current manager will receive your request</li>
                <li>• They can add you as a co-manager</li>
                <li>• You'll be notified when they respond</li>
              </ul>
            </div>

            <div className="space-y-3">
              <Button
                variant="primary"
                className="w-full"
                onClick={submitAccessRequest}
                loading={requestingAccess}
              >
                Send Access Request
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setRequestAccessTeam(null)}
              >
                Cancel
              </Button>
            </div>
          </Card>
        </Container>
      </div>
    )
  }

  // If user already has a team
  if (existingTeam) {
    return (
      <div className="min-h-screen bg-brand-cream py-12">
        <Container>
          <Card className="max-w-lg mx-auto p-6 text-center">
            <h2 className="font-heading text-xl font-bold text-brand-charcoal mb-4">
              You Already Have a Team
            </h2>
            <p className="text-brand-slate mb-4">
              You're already managing <strong>{existingTeam.name}</strong>. 
              You can only claim one team per account.
            </p>
            <div className="space-y-3">
              <Button
                variant="primary"
                className="w-full"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/scrimmages/manage')}
              >
                Manage Scrimmages
              </Button>
            </div>
          </Card>
        </Container>
      </div>
    )
  }

  // If we have a specific team to claim
  if (team) {
    return (
      <div className="min-h-screen bg-brand-cream py-12">
        <Container>
          <ClaimTeamFlow
            team={team}
            onClaimSuccess={handleClaimSuccess}
            onCancel={() => navigate('/scrimmages/bulletin')}
          />
        </Container>
      </div>
    )
  }

  // Search/browse mode
  return (
    <div className="min-h-screen bg-brand-cream py-12">
      <Container>
        <div className="max-w-2xl mx-auto">
          <h1 className="font-heading text-3xl font-bold text-brand-charcoal mb-2 text-center">
            Claim Your Team
          </h1>
          <p className="text-brand-slate text-center mb-8">
            Find your team from our database and claim it to manage your scrimmage postings.
          </p>

          {/* Search Form */}
          <Card className="p-6 mb-6">
            <form onSubmit={handleSearch}>
              <label className="block text-sm font-medium text-brand-charcoal mb-2">
                Search for your team
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter team name (e.g., Wayzata Squirt B1)"
                  className="input flex-1"
                />
                <Button type="submit" variant="primary">
                  Search
                </Button>
              </div>
            </form>
          </Card>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-3">
              <h2 className="font-heading text-lg font-semibold text-brand-charcoal">
                {searchResults.length} team{searchResults.length !== 1 ? 's' : ''} found
              </h2>
              {searchResults.map(t => (
                <Card 
                  key={t.id} 
                  className={`p-4 transition-colors ${t.is_claimed ? 'border-amber-300 bg-amber-50' : 'hover:border-brand-green'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-brand-charcoal">{t.name}</h3>
                        {t.is_claimed && (
                          <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                            Claimed
                          </span>
                        )}
                      </div>
                      {t.association_name && (
                        <p className="text-sm text-brand-slate">{t.association_name}</p>
                      )}
                      <p className="text-sm text-brand-slate">
                        {t.city}, {t.state} • {t.team_level}
                        {t.level_detail && ` ${t.level_detail}`}
                      </p>
                      {t.is_claimed && t.claimed_by_name && (
                        <p className="text-xs text-amber-700 mt-1">
                          Managed by {t.claimed_by_name}
                        </p>
                      )}
                    </div>
                    {t.is_claimed ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-amber-500 text-amber-700 hover:bg-amber-100"
                        onClick={() => handleRequestAccess(t)}
                      >
                        Request Access
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setTeam(t)
                          setSearchResults([])
                        }}
                      >
                        Claim
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* No Results */}
          {searchQuery && searchResults.length === 0 && !loading && (
            <Card className="p-6 text-center">
              <p className="text-brand-slate mb-4">
                No unclaimed teams found matching "{searchQuery}".
              </p>
              <p className="text-sm text-brand-slate">
                If your team isn't in our database yet, you can{' '}
                <a href="/auth?tab=signup" className="text-brand-green hover:underline">
                  create a new team account
                </a>
                {' '}instead.
              </p>
            </Card>
          )}

          {/* How it works */}
          <Card className="p-6 mt-6 bg-blue-50 border-blue-200">
            <h3 className="font-heading text-lg font-bold text-brand-charcoal mb-3">
              How Team Claiming Works
            </h3>
            <ol className="text-sm text-brand-slate space-y-2">
              <li>
                <strong>1. Find Your Team:</strong> Search for your team name above
              </li>
              <li>
                <strong>2. Verify Ownership:</strong> Upload your team calendar or request manual review
              </li>
              <li>
                <strong>3. Take Control:</strong> Manage your scrimmage postings and calendar
              </li>
            </ol>
            <p className="text-xs text-brand-slate mt-4">
              We automatically find teams by scanning public calendars. Claiming ensures you 
              have control over your team's scrimmage postings.
            </p>
          </Card>
        </div>
      </Container>
    </div>
  )
}
